import React from 'react';
import { t } from 'i18next';
import { Trans } from 'react-i18next';
import { parseLocaleStringInTimeZone, timeZone } from '@brighterion/utils/date-timezone';
import { useTemplateStore } from 'Hooks/zustand/useTemplateStore';
import { useGetScheduledDeploymentManually } from 'Hooks/query-hooks/scheduled-deployment/useGetScheduledDeployment';
import useScheduledDeploymentSummary from 'Hooks/query-hooks/scheduled-deployment/useScheduledDeploymentSummary';
import DeployCount from '../DeployButton/DeployCount';
import ScheduledDeploymentForm from './content/ScheduledDeploymentForm';
import { Delete } from './content/Delete';
import { IsLoading } from './content/IsLoading';
import { NoChanges } from './content/NoChanges';
import type { ScheduledDeploymentModalProps } from './ScheduledDeploymentModal';

export type ScheduledDeploymentContentProps = Omit<ScheduledDeploymentModalProps, 'templateRepositoryId' | 'options'>;

const ScheduledDeploymentContent = ({ closeModal, modal, status, rulesType }: ScheduledDeploymentContentProps) => {
    const { templateRepositoryId, locale } = useTemplateStore.getState().template;
    const scheduledDeployment = useGetScheduledDeploymentManually();
    const scheduledDeploymentSummary = useScheduledDeploymentSummary(templateRepositoryId);
    const scheduledDeploymentCount = scheduledDeploymentSummary.data?.current.totalUpdates ?? 0;

    if (scheduledDeployment.isLoading || scheduledDeploymentSummary.isLoading) {
        return <IsLoading closeModal={closeModal} modal={{ ...modal, name: 'loading' }} status={status} />;
    }

    if (modal.operation === 'delete' && scheduledDeployment.data && locale) {
        const getLocaleDate = parseLocaleStringInTimeZone(
            new Date(scheduledDeployment.data.scheduledTime),
            locale,
            timeZone
        );
        const { summary, totalUpdates } = scheduledDeployment.data;
        return (
            <Delete
                closeModal={closeModal}
                modal={{
                    ...modal,
                    title: t('scheduledDeployment.delete.title'),
                    name: 'delete'
                }}
                status={status}
                scheduledDeployment={scheduledDeployment.data}
            >
                <Trans i18nKey='scheduledDeployment.delete.content'>
                    Your deployment of <strong>{{ totalUpdates }} updates</strong> has been scheduled to deploy at{' '}
                    <strong>{{ getLocaleDate }}</strong>. Do you want to cancel this deployment?
                </Trans>
                <DeployCount prevDeploymentSummary={summary} newDeploymentSummary={summary} />
            </Delete>
        );
    }

    if (modal.operation === 'update' && scheduledDeployment.data && templateRepositoryId && locale) {
        const { version, summary } = scheduledDeployment.data;
        return (
            <ScheduledDeploymentForm
                closeModal={closeModal}
                modal={{
                    ...modal,
                    title: t('scheduledDeployment.update.title', { version }),
                    name: 'form'
                }}
                status={status}
                initialFormValues={{
                    comment: scheduledDeployment.data.comment,
                    deployNow: false,
                    scheduledTime: scheduledDeployment.data.scheduledTime,
                    templateRepositoryId
                }}
                locale={locale}
                rulesType={rulesType}
            >
                <Trans i18nKey='scheduledDeployment.update.content'>
                    Scheduled deployment of version {{ version }} contains the following updates:
                </Trans>
                <DeployCount prevDeploymentSummary={summary} newDeploymentSummary={summary} />
            </ScheduledDeploymentForm>
        );
    }

    if (
        modal.operation === 'create' &&
        scheduledDeploymentCount > 0 &&
        scheduledDeploymentSummary.data &&
        templateRepositoryId &&
        locale
    ) {
        return (
            <ScheduledDeploymentForm
                closeModal={closeModal}
                modal={{ ...modal, title: t('scheduledDeployment.create.title'), name: 'form' }}
                status={status}
                initialFormValues={{
                    comment: '',
                    deployNow: true,
                    scheduledTime: undefined,
                    templateRepositoryId
                }}
                locale={locale}
                rulesType={rulesType}
            >
                <p>{t('scheduledDeployment.create.content')}</p>
                <DeployCount
                    prevDeploymentSummary={
                        scheduledDeploymentSummary.data.previous ?? scheduledDeploymentSummary.data.current
                    }
                    newDeploymentSummary={scheduledDeploymentSummary.data.current}
                />
            </ScheduledDeploymentForm>
        );
    }

    if (scheduledDeployment.isError && templateRepositoryId && locale) {
        /** TODO: Return error message */
        return (
            <ScheduledDeploymentForm
                closeModal={closeModal}
                modal={modal}
                status={status}
                initialFormValues={{
                    comment: '',
                    deployNow: true,
                    scheduledTime: undefined,
                    templateRepositoryId
                }}
                locale={locale}
            />
        );
    }

    return <NoChanges closeModal={closeModal} modal={{ ...modal, name: 'no-changes' }} status={status} />;
};

export default ScheduledDeploymentContent;
